import { useEffect, useState } from 'react';
import { payrollReviewGateway } from '../../payroll-review';
import { viewerProcessedRun } from '../../payroll-review/approvalGuard';
import { useIdentity } from '../../identity/state/useIdentity';
import type { CustomPayRunResponse } from '../types';

/**
 * Whether the signed-in user sees this run as an approver: someone who did NOT create or submit it, so they are the "another
 * user" who may approve. Drives the dynamic third-step label ("Approve Payroll" vs "Pending Payroll") and who is shown
 * the Approve buttons. A view rule only — the backend still enforces separation of duties and the approve permission (it
 * has the final say, and a 403 leaves the screen read-only).
 *
 * The creator is known from the run itself; the submitter only once the run is in Payroll Review, so it is read from there
 * when a run has been handed off. Until that read settles (or if it is not permitted) the creator rule alone applies.
 */
export function useApproverView(run: CustomPayRunResponse | null): boolean {
  const { me } = useIdentity();
  const viewerUserId = me?.userId;
  const payrollRunId = run?.payrollRunId ?? null;
  const [submitter, setSubmitter] = useState<{ payrollRunId: string; userId: string | null } | null>(null);

  useEffect(() => {
    if (!payrollRunId) return;
    const controller = new AbortController();
    payrollReviewGateway
      .getRun(payrollRunId, controller.signal)
      .then((reviewRun) => {
        if (!controller.signal.aborted) setSubmitter({ payrollRunId, userId: reviewRun.submittedByUserId ?? null });
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [payrollRunId]);

  if (!run) return false;
  const submittedByUserId = submitter && submitter.payrollRunId === payrollRunId ? submitter.userId : null;
  return !viewerProcessedRun({ viewerUserId, createdByUserId: run.createdByUserId, submittedByUserId });
}
